import React, { useState } from 'react';
import { Download, FileArchive, Loader2, AlertCircle } from 'lucide-react';

const ImageDownload = ({ 
  assessmentId, 
  images = {}, 
  categoryFilter = 'all',
  className = '',
  buttonText = 'Download All',
  showOptions = true
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [error, setError] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Get API configuration from localStorage
  const getApiConfig = () => {
    try {
      const token = localStorage.getItem('properties_api_token') || '';
      const endpoint = localStorage.getItem('properties_api_endpoint') || '';
      return { token, endpoint };
    } catch (error) {
      console.error('Error getting API config:', error);
      return { token: '', endpoint: '' };
    }
  };

  // Calculate total images to download
  const getTotalImages = () => {
    if (categoryFilter === 'all') {
      return Object.values(images).reduce((total, categoryImages) => total + categoryImages.length, 0);
    }
    return images[categoryFilter] ? images[categoryFilter].length : 0;
  };

  const totalImages = getTotalImages();

  // Handle bulk download
  const handleBulkDownload = async (format = 'zip') => {
    if (totalImages === 0) {
      setError('No images to download');
      return;
    }

    setIsDownloading(true);
    setError('');
    setDownloadProgress(0);

    try {
      const { token, endpoint } = getApiConfig();
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['X-Properties-API-Token'] = token;
      }
      if (endpoint) {
        headers['X-Properties-API-Endpoint'] = endpoint;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/health-safety/hse-assessments/${assessmentId}/bulk-download`,
        {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            category_filter: categoryFilter === 'all' ? null : categoryFilter,
            format: format
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Download failed' }));
        throw new Error(errorData.detail || 'Download failed');
      }

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const blob = await response.blob();
      clearInterval(progressInterval);
      setDownloadProgress(100);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = response.headers.get('content-disposition')
        ?.split('filename=')[1]?.replace(/['"]/g, '') 
        || `assessment_${assessmentId}_images.${format}`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Reset state after successful download
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadProgress(0);
        setShowOptionsMenu(false);
      }, 1000);

    } catch (error) {
      console.error('Download error:', error);
      setError(error.message || 'Failed to download images');
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  // Handle individual image download
  const handleImageDownload = async (image) => {
    try {
      const imageData = image.data.startsWith('data:') 
        ? image.data 
        : `data:${image.mime_type || 'image/jpeg'};base64,${image.data}`;
      
      const link = document.createElement('a');
      link.href = imageData;
      link.download = image.file_name || `image_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Image download error:', error);
      setError('Failed to download image');
    }
  };

  // Render download options menu
  const renderOptionsMenu = () => {
    if (!showOptions || totalImages <= 1) return null;

    return (
      <div className="relative">
        <button
          onClick={() => setShowOptionsMenu(!showOptionsMenu)}
          className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          disabled={isDownloading}
        >
          <Download className="w-4 h-4" />
          <span className="text-sm">Options</span>
        </button>

        {showOptionsMenu && (
          <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
            <div className="py-1">
              <button
                onClick={() => handleBulkDownload('zip')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                disabled={isDownloading}
              >
                <FileArchive className="w-4 h-4" />
                <span>Download as ZIP</span>
              </button>
              <button
                onClick={() => handleBulkDownload('individual')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                disabled={isDownloading}
              >
                <Download className="w-4 h-4" />
                <span>Download Individual</span>
              </button>
            </div>
          </div>
        )}

        {showOptionsMenu && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowOptionsMenu(false)}
          />
        )}
      </div>
    );
  };

  if (totalImages === 0) {
    return null;
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Main download button */}
      <button
        onClick={() => handleBulkDownload('zip')}
        disabled={isDownloading}
        className={`flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
          isDownloading ? 'cursor-progress' : ''
        }`}
      >
        {isDownloading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        <span className="text-sm">
          {isDownloading 
            ? `Downloading... ${downloadProgress}%` 
            : `${buttonText} (${totalImages})`
          }
        </span>
      </button>

      {/* Options menu */}
      {renderOptionsMenu()}

      {/* Error message */}
      {error && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ImageDownload;